import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { GalleryGrid } from "@/components/GalleryGrid";
import { Reveal, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";
import { useLang, localizePrestation, POLE_EN } from "@/i18n";

const POLE_ID_BY_NAME = { Personnaliser: "personnaliser", Peindre: "peindre", Transmettre: "transmettre" };

export default function PrestationDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [raw, setRaw] = useState(null);
  const [works, setWorks] = useState([]);

  useEffect(() => {
    setRaw(null);
    api.get(`/prestations/${slug}`).then((res) => setRaw(res.data)).catch(() => setRaw(false));
    api.get("/gallery", { params: { prestation: slug } }).then((res) => setWorks(res.data)).catch(() => {});
  }, [slug]);

  if (raw === false) {
    return (
      <div>
        <Nav />
        <main className="pt-40 pb-28 min-h-screen max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="font-serif text-4xl font-light mb-6">{t("pr.notFound")}</h1>
          <Link to="/prestations" className="text-gold text-sm uppercase tracking-[0.15em] inline-flex items-center gap-2">
            <ArrowLeft size={16} /> {t("pr.all")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!raw) {
    return <div className="min-h-screen flex items-center justify-center text-white/40 font-mono text-sm">…</div>;
  }

  const p = localizePrestation(raw, lang);
  const poleName = lang === "en" ? (POLE_EN[POLE_ID_BY_NAME[raw.pole]]?.name || raw.pole) : raw.pole;

  return (
    <div>
      <Nav />
      <main className="pt-36 pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Link to="/prestations" data-testid="back-to-prestations"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-white/40 hover:text-gold transition-colors duration-300 mb-10">
              <ArrowLeft size={14} /> {t("pr.back")}
            </Link>
            <Overline>{poleName} — /{String(p.order).padStart(2, "0")}</Overline>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter uppercase mb-4" data-testid="prestation-title">
              {p.title}
            </h1>
            <p className="font-serif text-2xl lg:text-3xl text-gold italic font-light mb-10">{p.accroche}</p>
            <p className="text-white/70 max-w-2xl leading-relaxed text-lg mb-16">{p.description}</p>
          </Reveal>

          {p.details?.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-px bg-white/10 mb-24" data-testid="prestation-details">
              {p.details.map((d, i) => (
                <Reveal key={`${d.label}-${i}`} delay={i * 0.05}>
                  <div className="bg-[#0a0a0a] p-8 h-full">
                    <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-gold mb-3">{d.label}</p>
                    <p className="text-white/70 text-sm leading-relaxed">{d.value}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          <Reveal className="mb-24">
            <Link to={`/contact?prestation=${encodeURIComponent(raw.title)}`} data-testid="prestation-cta-devis"
              className="inline-flex items-center gap-3 bg-gold text-black px-10 py-5 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:bg-gold-light">
              {p.cta || t("pr.defaultCta")} <ArrowUpRight size={16} />
            </Link>
          </Reveal>

          <div>
            <Reveal className="mb-10">
              <Overline>{t("ov.galleryDedic")}</Overline>
              <h2 className="font-serif text-2xl lg:text-3xl font-light">{t("pr.works")} — {p.title}</h2>
            </Reveal>
            {works.length === 0 ? (
              <p className="text-white/40 py-10 border-t border-white/10" data-testid="prestation-gallery-empty">
                {t("pr.empty")}
              </p>
            ) : (
              <GalleryGrid items={works} />
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
